import { fetchAuthSession } from '@aws-amplify/auth';

const API_BASE_URL = process.env.API_BASE_URL || "https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api";
const apiKey = process.env.REACT_APP_MONGO_API_KEY;

let authToken = localStorage.getItem("access_token") || null;

export function setAuthToken(token) {
  authToken = token;
  if (token) localStorage.setItem("access_token", token);
  else localStorage.removeItem("access_token");
}

export function clearAuthToken() {
  authToken = null;
  localStorage.removeItem("access_token");
}

async function authHeaders() {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = session.tokens?.idToken;
    if (idToken) {
      return { Authorization: `Bearer ${idToken.toString()}` };
    }
    return {};
  } catch (error) {
    console.error('Error fetching auth session:', error);
    return {};
  }
}

async function handleResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || "API request failed");
    err.status = response.status;
    throw err;
  }
  return data;
}

export async function apiGet(endpoint, requireAuth = false) {
  const headers = { "Content-Type": "application/json", ...(requireAuth ? await authHeaders() : {}) };
  const res = await fetch(`${API_BASE_URL}${endpoint}`, { method: "GET", headers });
  return handleResponse(res);
}

export const apiPost = async (endpoint, data = {}, requiresAuth = false) => {
  const config = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
    body: JSON.stringify(data),
  };

  if (requiresAuth) {
    config.headers = { ...config.headers, ...await authHeaders() };
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return await response.json();
};

export async function apiPut(endpoint, body = {}, requireAuth = true) {
  const headers = { "Content-Type": "application/json", 'x-api-key': apiKey, ...(requireAuth ? await authHeaders() : {}) };
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "PUT", headers, body: JSON.stringify(body)
  });
  return handleResponse(res);
}

export const apiDelete = async (endpoint, requiresAuth = false) => {
  const config = {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
  };

  if (requiresAuth) {
    config.headers = { ...config.headers, ...await authHeaders() };
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return await response.json();
};

export const getMovies = async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiGet(`/movies?${queryString}`);
}

// Cache for movie/show details
const movieCache = new Map();
const MOVIE_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

export const getMovieById = async (movieId) => {
    const cacheKey = `movie_${movieId}`;
    const cached = movieCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < MOVIE_CACHE_TTL) {
        return cached.data;
    }
    
    const data = await apiGet(`/movies/${movieId}`);
    movieCache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
};

export const getShows = (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiGet(`/shows?${queryString}`);
}

export const getShowById = async (showId) => {
    const cacheKey = `show_${showId}`;
    const cached = movieCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < MOVIE_CACHE_TTL) {
        return cached.data;
    }
    
    const data = await apiGet(`/shows/${showId}`);
    movieCache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
};

// Batch fetch movies
export const getMoviesByIds = async (movieIds) => {
    const results = {};
    const uncachedIds = [];
    
    // Check cache first
    movieIds.forEach(id => {
        const cacheKey = `movie_${id}`;
        const cached = movieCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < MOVIE_CACHE_TTL) {
            results[id] = cached.data;
        } else {
            uncachedIds.push(id);
        }
    });
    
    // Fetch uncached in parallel (max 5 at a time)
    const batchSize = 5;
    for (let i = 0; i < uncachedIds.length; i += batchSize) {
        const batch = uncachedIds.slice(i, i + batchSize);
        await Promise.all(
            batch.map(async id => {
                try {
                    const data = await apiGet(`/movies/${id}`);
                    movieCache.set(`movie_${id}`, { data, timestamp: Date.now() });
                    results[id] = data;
                } catch (err) {
                    console.error(`Error fetching movie ${id}:`, err);
                }
            })
        );
    }
    
    return results;
};

// Clear movie cache
export const clearMovieCache = () => {
    movieCache.clear();
};

// Periodic cache cleanup
setInterval(() => {
    const now = Date.now();
    for (const [key, value] of movieCache.entries()) {
        if (now - value.timestamp > MOVIE_CACHE_TTL) {
            movieCache.delete(key);
        }
    }
}, 60 * 1000);
